import React from "react";
import { compose, withPropsOnChange, withHandlers } from "recompose";
import { Control, track, actions } from "react-redux-form";
import {
  FormGroup,
  withLocalForm,
  SubmitButton,
  onMount,
  required,
  withReload,
} from "components/common";
import { Button } from "reactstrap";
import { withXeroErrors } from "components";
import { XeroClient } from "services";
import { set, get } from "lodash";
import shortid from "shortid";
import { Trans, withTranslation } from "react-i18next";
import deepPick from "deepPick";
import { NotificationManager } from "react-notifications";

export const TrackingCategoryEdit = ({
  state,
  LocalForm,
  onSubmit,
  onDelete,
  onAddOption,
  XeroErrors,
  onTrackingCategoryDelete,
}) => (
  <LocalForm
    onSubmit={onSubmit}
    validators={{
      Name: { required },
      "Options[].Name": { required },
    }}
  >
    <XeroErrors />
    <FormGroup label="Tracking category name" model=".Name">
      <Control.text
        model=".Name"
        style={{ width: "20rem" }}
        className="form-control-sm pb-2 pt-2"
      />
    </FormGroup>
    <FormGroup label="Category options" className="pb-2 pt-2">
      {state.Options.map(({ key, deleted }, idx) => {
        if (deleted) return null;

        const tracker = track(".Options[].Name", { key });

        return (
          <FormGroup key={key} model={tracker} className="pb-2 pt-2">
            <div className="row no-gutters align-items-center">
              <div className="col">
                <Control.text model={tracker} className="form-control-sm" />
              </div>
              <div className="col-auto">
                <Button
                  size="sm"
                  color="danger"
                  className="ml-2"
                  onClick={() => onDelete(idx)}
                >
                  <span className="fa fa-trash" />
                </Button>
              </div>
            </div>
          </FormGroup>
        );
      })}
    </FormGroup>
    <Button size="sm" className="btn btn-success" onClick={onAddOption}>
      <Trans><i className="fa fa-plus mr-2"></i>Add another item</Trans>
    </Button>
    <div className="d-flex mt-3">
      {!!state.TrackingCategoryID && (
      <Button onClick={onTrackingCategoryDelete} color="danger">
        <Trans>Delete</Trans>
      </Button>
      )}
      <div className="mx-auto"/>
      <SubmitButton>Save</SubmitButton>
    </div>
  </LocalForm>
);

export const enhance = compose(
  withReload(),
  withTranslation(),
  withXeroErrors(),
  onMount(({ match }) => () => {
    const xero = XeroClient();
    const { id } = match.params;

    return Promise.props({
      state:
        id === "new"
          ? {
              Name: "",
              Status: "ACTIVE",
              Options: [
                {
                  Name: "",
                  Status: "ACTIVE",
                  key: shortid(),
                },
                {
                  Name: "",
                  Status: "ACTIVE",
                  key: shortid(),
                },
                {
                  Name: "",
                  Status: "ACTIVE",
                  key: shortid(),
                },
              ],
            }
          : xero.get(`trackingcategories/${id}`).get("data"),
      xero,
    });
  }),
  withPropsOnChange([], ({ state }) => ({
    state: set(
      state,
      "Options",
      state.Options.map((i) => set(i, "key", shortid()))
    ),
  })),
  withLocalForm({
    initialState: (props) => props.state,
  }),
  withHandlers({
    onSubmit:
      ({ dispatch, onSaved, xero, setXeroErrors, t, reload, history, match }) =>
      (tmpItem) => {
        let item = deepPick(tmpItem, [
          "Name",
          "TrackingCategoryID",
          "Status",
          "Options.Name",
          "Options.TrackingOptionID",
          "Options.deleted",
          "Options.Status",
        ]);
        let { Options, ...category } = item;

        return (
          item.TrackingCategoryID
            ? xero.put(
                `trackingcategories/${category.TrackingCategoryID}`,
                category
              )
            : xero.post("trackingcategories", category)
        )
          .then((resp) => {
            const id = resp.data.TrackingCategoryID;
            dispatch(actions.change("local.TrackingCategoryID", id));
            const url = `trackingcategories/${id}/options`;

            return Promise.mapSeries(Options, ({ deleted, ...o }, idx) => {
              let p = null;
              if (deleted && !o.TrackingOptionID) return null;

              if (deleted) {
                p = xero.post(url, {
                  deleted,
                  ...o,
                });
              } else if (o.TrackingOptionID) {
                p = xero.post(url, o);
              } else {
                p = xero.put(url, o);
              }

              return p.then((resp) => {
                const id = get(resp, "data.TrackingOptionID");
                if (id)
                  dispatch(
                    actions.change(`local.Options[${idx}].TrackingOptionID`, id)
                  );
              });
            }).then(() => {
              NotificationManager.success(t("Saved successfully"));
              history.replace(match.path.replace(":id", id));
              reload();
              if (onSaved) onSaved();
            });
          })
          .catch(setXeroErrors)
          .finally(() => dispatch(actions.setPending("local", false)));
      },
    onDelete:
      ({ dispatch }) =>
      (idx) => {
        dispatch(actions.change(`local.Options[${idx}].deleted`, true));
        dispatch(actions.change(`local.Options[${idx}].Name`, "DELETED"));
      },
    onAddOption:
      ({ dispatch }) =>
      () => {
        dispatch(
          actions.push("local.Options", {
            key: shortid(),
            Name: "",
            Status: "ACTIVE",
          })
        );
      },
    onTrackingCategoryDelete:
      ({ xero, state, history, onSaved }) =>
      () => {
        xero
          .delete(`trackingcategories/${state.TrackingCategoryID}`)
          .then(() => {
            if (onSaved) {
              onSaved().then(() => history.replace("/settings/tracking"));
            } else {
              history.replace("/settings/tracking");
            }
          });
      },
  })
);

export default enhance(TrackingCategoryEdit);
